// Send-time offer parsing — THE offer-integrity guard for the retention queue.
//
// Invariant (bidirectional): the credit filed on send must always equal the
// dollar promise in the FINAL text, and a promised discount can never go out
// unrecorded. The nightly/on-demand draft's offer_amount (worker
// src/drafts.ts) is only the INTENT that seeded the initial textarea; what
// actually gets filed is decided here, from whatever the operator sends.

/** Chars of surrounding context that count as "within a few words". */
const NEAR = 30;

/** Offer language for proximity checks (either order around the amount). */
const OFFER_WORD_RE = /\b(?:off|credit|discount)\b/i;

function offerWordNear(text: string, index: number, length: number): boolean {
  const start = Math.max(0, index - NEAR);
  const end = Math.min(text.length, index + length + NEAR);
  return OFFER_WORD_RE.test(text.slice(start, end));
}

/** Spelled-out amounts accepted next to offer language, most specific first
 *  (so "two hundred" never reads as 100, "twenty-five" never as 20). */
const SPELLED: Array<[RegExp, number]> = [
  [/\btwo\s+hundred\b/i, 200],
  [/\b(?:a\s+|one\s+)?hundred\b/i, 100],
  [/\bseventy[\s-]?five\b/i, 75],
  [/\bfifty\b/i, 50],
  [/\btwenty[\s-]?five\b/i, 25],
  [/\btwenty\b/i, 20],
  [/\bten\b/i, 10],
];

/**
 * True when the text contains offer language at all: "credit" or "discount"
 * anywhere, or "off" (too common a word to count alone) within a few words of
 * something money-ish ($, %, a digit, dollars/bucks, or a spelled amount).
 *
 * Used at send time to catch a promise whose amount extractOfferAmount cannot
 * read (e.g. "a discount", "20% off"): those block with a confirm dialog
 * instead of going out silently unrecorded.
 */
export function hasOfferLanguage(text: string): boolean {
  if (/\b(?:credit|discount)\b/i.test(text)) return true;
  const offRe = /\boff\b/gi;
  let m: RegExpExecArray | null;
  while ((m = offRe.exec(text)) !== null) {
    const start = Math.max(0, m.index - NEAR);
    const end = Math.min(text.length, m.index + m[0].length + NEAR);
    const ctx = text.slice(start, end);
    if (/[$%]|\d|\b(?:dollars?|bucks?|hundred|fifty|twenty|seventy|ten)\b/i.test(ctx)) {
      return true;
    }
  }
  return false;
}

/**
 * The dollar amount of a promised discount/credit in the (possibly edited)
 * text, or null. This is what actually gets FILED as a client credit at send
 * time — regardless of the draft's offer_amount intent, any color, any
 * source — so the filed credit always equals the promise in the final text.
 *
 * Matches, in order:
 *   1. the first $<digits> within a few words of offer language
 *      ("$100 off", "a $75 credit", "we'll credit you $50");
 *   2. the first "<digits> dollars/bucks" near offer language
 *      ("100 dollars off", "50 bucks credit");
 *   3. common spelled-out amounts near offer language
 *      ("hundred bucks off" → 100, "fifty off" → 50).
 *
 * Null when there is no offer language, or when the amount cannot be read —
 * the send flow then distinguishes the two via hasOfferLanguage.
 */
export function extractOfferAmount(text: string): number | null {
  if (!hasOfferLanguage(text)) return null;

  // 1. $-amounts — the first one near offer language wins.
  const dollarRe = /\$\s*(\d[\d,]*(?:\.\d+)?)/g;
  let m: RegExpExecArray | null;
  while ((m = dollarRe.exec(text)) !== null) {
    if (!offerWordNear(text, m.index, m[0].length)) continue;
    const amount = Number.parseFloat(m[1].replace(/,/g, ""));
    if (Number.isFinite(amount) && amount > 0) return amount;
  }

  // 2. "<digits> dollars/bucks" near offer language.
  const wordedRe = /\b(\d[\d,]*(?:\.\d+)?)\s*(?:dollars?|bucks?)\b/gi;
  while ((m = wordedRe.exec(text)) !== null) {
    if (!offerWordNear(text, m.index, m[0].length)) continue;
    const amount = Number.parseFloat(m[1].replace(/,/g, ""));
    if (Number.isFinite(amount) && amount > 0) return amount;
  }

  // 3. Spelled-out fallbacks ("hundred bucks off", "fifty off").
  for (const [re, value] of SPELLED) {
    const sm = re.exec(text);
    if (sm && offerWordNear(text, sm.index, sm[0].length)) return value;
  }

  return null;
}
